/**
 * ServiceTemplateSelector
 *
 * Renders a filter-pills bar + search input (sticky within its scroll
 * container) followed by a natural-flow card grid. Works both inside the
 * creation dialog (where the parent provides overflow-y-auto) and on the
 * full-page route (where the Layout's <main> is the scroll root).
 */

import {
  BarChart,
  Brain,
  Code,
  Database,
  Globe,
  HardDrive,
  Layers,
  Search,
  Server,
  Tag,
  Users,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { ServiceCatalogItem } from "../../../types/service-catalog";
import { ServiceTemplateCard } from "../../common";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Skeleton } from "../../ui/skeleton";
import { FALLBACK_SERVICE_TYPES, ServiceTemplateSelectorProps } from "./service-creation-types";

// ── Category metadata ─────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { label: string; Icon: React.ComponentType<any> }> = {
  all: { label: "All", Icon: Layers },
  "web-applications": { label: "Web Apps", Icon: Globe },
  databases: { label: "Databases", Icon: Database },
  development: { label: "Development", Icon: Code },
  "ai-ml": { label: "AI / ML", Icon: Brain },
  collaboration: { label: "Collaboration", Icon: Users },
  infrastructure: { label: "Infrastructure", Icon: Server },
  devops: { label: "DevOps", Icon: Layers },
  analytics: { label: "Analytics", Icon: BarChart },
  "object-storage": { label: "Storage", Icon: HardDrive },
};

function getCategoryMeta(cat: string) {
  return CATEGORY_META[cat] ?? { label: cat.replace(/-/g, " "), Icon: Server };
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-lg bg-card p-4 flex items-start gap-3">
      <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2 pt-0.5">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
        <div className="flex gap-1 mt-1">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-10" />
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ServiceTemplateSelector({
  serviceCatalog,
  serviceCatalogLoading,
  searchResults,
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  onTemplateSelect,
  selectedTemplate,
}: ServiceTemplateSelectorProps) {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  // Merge real catalog with static fallback
  const fallbackCatalog = {
    "Web Applications": FALLBACK_SERVICE_TYPES.filter(
      (t) => t.category === "Web Applications"
    ) as ServiceCatalogItem[],
    Databases: FALLBACK_SERVICE_TYPES.filter(
      (t) => t.category === "Databases"
    ) as ServiceCatalogItem[],
  };

  // Sanitize: values must be arrays. The backend previously returned a flat
  // array; if that shape leaks in, Object.entries gives ["0", item] pairs and
  // item.map() crashes. Filter out any non-array values to fall back safely.
  const allTemplates: Record<string, ServiceCatalogItem[]> = useMemo(() => {
    const entries = Object.entries(serviceCatalog ?? {}).filter(
      ([, v]) => Array.isArray(v) && v.length > 0
    );
    return entries.length > 0
      ? Object.fromEntries(entries) as Record<string, ServiceCatalogItem[]>
      : fallbackCatalog;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCatalog]);

  const hasRealCatalog = Object.entries(serviceCatalog ?? {}).some(([, v]) => Array.isArray(v));

  // Filter pills: "All" + one per category, each with its count
  const filterPills = useMemo(() => {
    const total = Object.values(allTemplates).reduce(
      (sum, ts) => sum + ts.length,
      0
    );
    return [
      { key: "all", count: total },
      ...Object.entries(allTemplates).map(([key, ts]) => ({
        key,
        count: ts.length,
      })),
    ];
  }, [allTemplates]);

  // Templates visible in the current filter + search state
  const visibleByCategory: Record<string, ServiceCatalogItem[]> = useMemo(() => {
    if (searchQuery.trim()) {
      const filtered =
        selectedCategory === "all"
          ? searchResults
          : searchResults.filter((t) => t.category === selectedCategory);
      return filtered.length ? { "Search Results": filtered } : {};
    }
    if (selectedCategory === "all") return allTemplates;
    const match = Object.entries(allTemplates).find(
      ([key]) =>
        key === selectedCategory ||
        key.toLowerCase().replace(/\s+/g, "-") === selectedCategory
    );
    return match ? { [match[0]]: match[1] } : {};
  }, [allTemplates, selectedCategory, searchQuery, searchResults]);

  // Popular services shown at the top when browsing All
  const popularTemplates = useMemo(
    () =>
      Object.values(allTemplates)
        .flat()
        .filter((t) => t.isPopular && !t.isComingSoon)
        .slice(0, 4),
    [allTemplates]
  );

  const showFeatured =
    !searchQuery.trim() &&
    selectedCategory === "all" &&
    popularTemplates.length > 0;

  // Collect popular tags from the visible templates (capped at 12)
  const availableTags = useMemo(() => {
    const allItems = Object.values(allTemplates).flat().filter((t) => !t.isComingSoon);
    const counts = new Map<string, number>();
    for (const t of allItems) {
      for (const tag of t.tags ?? []) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([tag]) => tag);
  }, [allTemplates]);

  // Apply tag filter on top of category filter
  const tagFilteredByCategory = useMemo(() => {
    if (!selectedTag) return visibleByCategory;
    const out: Record<string, ServiceCatalogItem[]> = {};
    for (const [cat, items] of Object.entries(visibleByCategory)) {
      const filtered = items.filter((t) => (t.tags ?? []).includes(selectedTag));
      if (filtered.length) out[cat] = filtered;
    }
    return out;
  }, [visibleByCategory, selectedTag]);

  return (
    <div className="flex flex-col">
      {/*
       * Sticky bar: filter pills + search.
       * `sticky top-0` works relative to the nearest scroll ancestor — the
       * dialog's overflow-y-auto body or the Layout's <main>.
       */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 pt-3 pb-3 space-y-3">
        {/* Scrollable filter pills — hidden scrollbar keeps it clean */}
        <div
          className="flex gap-2 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          {filterPills.map(({ key, count }) => {
            const { label, Icon } = getCategoryMeta(key);
            const isActive = selectedCategory === key;
            return (
              <Button
                key={key}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => { onCategoryChange(key); setSelectedTag(null); }}
                className="shrink-0 h-8 gap-1.5 text-xs"
              >
                <Icon className="h-3 w-3" />
                {label}
                <Badge
                  variant={isActive ? "secondary" : "outline"}
                  className="ml-0.5 h-4 px-1.5 text-2xs font-normal"
                >
                  {count}
                </Badge>
              </Button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search services..."
            className="pl-9 h-9 text-sm"
            value={searchQuery}
            onChange={(e) => {
              onSearchChange(e.target.value);
              setSelectedTag(null);
            }}
          />
        </div>

        {/* Tag pills — only show when not searching */}
        {!searchQuery.trim() && availableTags.length > 0 && (
          <div
            className="flex gap-1.5 overflow-x-auto pb-0.5"
            style={{ scrollbarWidth: "none" }}
          >
            <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
            {availableTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-2xs font-medium border transition-colors ${
                  selectedTag === tag
                    ? "bg-primary text-primary-foreground border-primary"
                    : "text-muted-foreground border-border hover:border-foreground hover:text-foreground"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Card grid — natural flow, no fixed height */}
      <div className="p-4 space-y-6">
        {/* Loading skeletons */}
        {serviceCatalogLoading && (
          <>
            <div>
              <Skeleton className="h-3 w-24 mb-3" />
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            </div>
            <div>
              <Skeleton className="h-3 w-20 mb-3" />
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            </div>
          </>
        )}

        {/* Empty state */}
        {!serviceCatalogLoading && Object.keys(tagFilteredByCategory).length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Search className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No services found.</p>
            {searchQuery && (
              <Button
                variant="link"
                size="sm"
                className="mt-1 h-auto p-0 text-xs"
                onClick={() => onSearchChange("")}
              >
                Clear search
              </Button>
            )}
          </div>
        )}

        {!serviceCatalogLoading && (
          <>
            {/* Featured section — only when browsing All with no search */}
            {showFeatured && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Popular
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {popularTemplates.map((template) => (
                    <ServiceTemplateCard
                      key={template.id}
                      template={template}
                      onSelect={onTemplateSelect}
                      selected={selectedTemplate?.id === template.id}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Category sections */}
            {Object.entries(tagFilteredByCategory).map(([category, templates]) => (
              <div key={category}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  {category === "Search Results"
                    ? `${templates.length} result${templates.length !== 1 ? "s" : ""} for "${searchQuery}"`
                    : category.replace(/-/g, " ")}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {templates.map((template: ServiceCatalogItem) => (
                    <ServiceTemplateCard
                      key={template.id}
                      template={template}
                      onSelect={onTemplateSelect}
                      selected={selectedTemplate?.id === template.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
